import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslator } from '@/hooks/useTranslator';

interface TermsDeleteAccountDialogProps {
  open: boolean;
  confirmText: string;
  onConfirmTextChange: (value: string) => void;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Same confirmation UI as the profile page's own delete-account flow
 *  (`ProfileSettings.tsx`) — reused verbatim, including its i18n copy, so a
 *  user sees identical wording whether they delete from the gate or from
 *  settings. */
export function TermsDeleteAccountDialog({
  open,
  confirmText,
  onConfirmTextChange,
  isPending,
  onCancel,
  onConfirm,
}: TermsDeleteAccountDialogProps) {
  const { getTranslation: t } = useTranslator({ page: 'profile' });

  return (
    <AlertDialog
      open={open}
      loading={isPending}
      onOpenChange={next => !next && onCancel()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('settings.deleteAccount.confirmTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('settings.deleteAccount.confirmDesc')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className='py-2'>
          <Label className='text-xs font-medium text-foreground'>
            {t('settings.deleteAccount.typeDelete')}
          </Label>
          <Input
            value={confirmText}
            onChange={e => onConfirmTextChange(e.target.value)}
            placeholder='delete'
            className='mt-1.5'
          />
        </div>
        <AlertDialogFooter>
          <Button variant='outline' onClick={onCancel}>
            {t('settings.deleteAccount.cancel')}
          </Button>
          <Button
            variant='destructive'
            loading={isPending}
            disabled={confirmText !== 'delete'}
            onClick={onConfirm}
          >
            {isPending
              ? t('settings.deleteAccount.deleting')
              : t('settings.deleteAccount.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
